import { ValidationError } from './validation';
import { DetailTemplate } from '../schema';
import { toast } from '@/hooks/use-toast';

export interface ColumnMapping {
  key: keyof DetailTemplate;
  index: number;
  header: string;
}

const HEADERS = {
  STT: 'STT*',
  STT_IN: 'STT in',
  MA_SO: 'Mã chỉ tiêu*',
  MA_IN: 'Mã in',
  CHI_TIEU: 'Tên chỉ tiêu*',
  CHI_TIEU2: 'Tên khác',
  CHI_TIEU3: 'Tên 3',
  THUYET_MINH: 'Thuyết minh',
  TS_NV: 'Phân loại',
  IN_YN: 'In',
  BOLD_YN: 'Kiểu chữ',
  LOAI: 'Loại dữ liệu',
  NO_CO: 'Loại phát sinh',
  DAU_CUOI: 'Thời điểm',
  CONG_NO: 'Loại',
  DAU: 'Giá trị',
  TK: 'TK',
  TK_DU: 'TK đối ứng',
  TK_GT: 'TK giảm trừ',
  CONG_THUC: 'Công thức',
  DON_VI: 'Đơn vị cơ sở',
  KHACH_HANG: 'Khách hàng',
  TAI_KHOAN: 'Tài khoản',
  HOP_DONG: 'Hợp đồng',
  KHE_UOC: 'Khế ước',
  VU_VIEC: 'Vụ việc',
  PHI: 'Phí',
  BO_PHAN: 'Bộ phận'
} as const;

const CONDITION_MAPPING = [
  { header: HEADERS.DON_VI, id: 1 },
  { header: HEADERS.KHACH_HANG, id: 2 },
  { header: HEADERS.TAI_KHOAN, id: 3 },
  { header: HEADERS.HOP_DONG, id: 4 },
  { header: HEADERS.KHE_UOC, id: 5 },
  { header: HEADERS.VU_VIEC, id: 6 },
  { header: HEADERS.PHI, id: 7 },
  { header: HEADERS.BO_PHAN, id: 8 }
];

const MAPPINGS = {
  TS_NV: { 'Tài sản': '1', 'Nguồn vốn': '2' },
  IN_YN: { Có: '1', Không: '0' },
  BOLD_YN: { Đậm: '1', 'Không đậm': '0' },
  LOAI: {
    'Lấy theo mã số hoặc lập công thức': '0',
    'Lấy số dư tài khoản': '1',
    'Lấy phát sinh tài khoản sổ cái': '2',
    'Lấy phát sinh tài khoản giá thành vụ việc': '3',
    'Lấy phát sinh tài khoản sổ cái và giá thành vụ việc': '4'
  },
  NO_CO: { Nợ: '1', Có: '2', 'Nợ trừ Có': '3', 'Có trừ Nợ': '4' },
  DAU_CUOI: { 'Đầu kỳ': '1', 'Cuối kỳ': '2' },
  CONG_NO: {
    'Không lấy chi tiết 1 vế của đối tượng công nợ': '0',
    'Lấy chi tiết 1 vế của đối tượng công nợ': '1'
  },
  DAU: { 'Bình thường': '0', 'Không âm': '1', 'Đảo dấu': '2' }
};

const createColumnMapping = (headers: any[]): Record<string, ColumnMapping> => {
  const mapping: Record<string, ColumnMapping> = {};
  const headerConfig: Array<{ header: string; key: keyof DetailTemplate }> = [
    { header: HEADERS.STT, key: 'stt' },
    { header: HEADERS.STT_IN, key: 'stt_in' },
    { header: HEADERS.MA_SO, key: 'ma_so' },
    { header: HEADERS.MA_IN, key: 'ma_in' },
    { header: HEADERS.CHI_TIEU, key: 'chi_tieu' },
    { header: HEADERS.CHI_TIEU2, key: 'chi_tieu2' },
    { header: HEADERS.CHI_TIEU3, key: 'chi_tieu3' },
    { header: HEADERS.THUYET_MINH, key: 'thuyet_minh' },
    { header: HEADERS.TS_NV, key: 'ts_nv' },
    { header: HEADERS.IN_YN, key: 'in_yn' },
    { header: HEADERS.BOLD_YN, key: 'bold_yn' },
    { header: HEADERS.LOAI, key: 'loai' },
    { header: HEADERS.NO_CO, key: 'no_co' },
    { header: HEADERS.DAU_CUOI, key: 'dau_cuoi' },
    { header: HEADERS.CONG_NO, key: 'cong_no' },
    { header: HEADERS.DAU, key: 'dau' },
    { header: HEADERS.TK, key: 'tk' },
    { header: HEADERS.TK_DU, key: 'tk_du' },
    { header: HEADERS.TK_GT, key: 'tk_gt' },
    { header: HEADERS.CONG_THUC, key: 'cong_thuc' }
  ];

  headers.forEach((header, index) => {
    const cleanHeader = String(header).trim();
    const config = headerConfig.find(c => c.header === cleanHeader);

    if (config) {
      mapping[config.key] = {
        key: config.key,
        index,
        header: cleanHeader
      };
    } else {
      // Check for condition columns
      const condition = CONDITION_MAPPING.find(c => c.header === cleanHeader);
      if (condition) {
        mapping[`condition_${condition.id}`] = {
          key: `condition_${condition.id}` as any,
          index,
          header: cleanHeader
        };
      }
    }
  });

  return mapping;
};

const parseExcelFile = async (file: File): Promise<{ headers: any[]; dataRows: any[][] }> => {
  const XLSX = await import('xlsx');
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('Không tìm thấy worksheet');
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

  if (jsonData.length === 0) {
    throw new Error('File không có dữ liệu');
  }

  const headers = jsonData[0];
  const dataRows = jsonData.slice(1);

  return { headers, dataRows };
};

const mapValue = (
  value: any,
  mapping: Record<string, string>,
  defaultValue: string,
  onError?: (label: string) => void
): string => {
  if (value === null || value === undefined || value === '') return defaultValue;

  const strValue = String(value).trim().normalize('NFC');

  // Check if it's already a valid code
  const validCodes = Object.values(mapping);
  if (validCodes.includes(strValue)) {
    return strValue;
  }

  // Normalize mapping keys for robust matching
  const normalizedMapping: Record<string, string> = {};
  Object.entries(mapping).forEach(([key, val]) => {
    normalizedMapping[key.normalize('NFC')] = val;
  });

  const result = normalizedMapping[strValue];
  if (result === undefined && onError) {
    onError(strValue);
    return defaultValue;
  }

  return result || defaultValue;
};

const splitCodes = (value: any): string[] => {
  const str = String(value || '').trim();
  if (!str) return [];
  return str.split(/\s*,\s*/).filter(Boolean);
};

export const handleImportExcel = async (
  onSubmit: (data: DetailTemplate[]) => Promise<void>,
  onValidationErrors?: (args: { errors: ValidationError[]; columnMapping: Record<string, ColumnMapping> }) => void
): Promise<void> => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.xlsx,.xls';

  input.onchange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
      const { headers, dataRows } = await parseExcelFile(file);
      const columnMapping = createColumnMapping(headers);

      if (!columnMapping.ma_so || !columnMapping.chi_tieu) {
        toast({
          description: 'Không tìm thấy các cột bắt buộc (Mã chỉ tiêu, Tên chỉ tiêu)',
          variant: 'destructive'
        });
        return;
      }

      const validationErrors: ValidationError[] = [];
      const templates: DetailTemplate[] = dataRows
        .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''))
        .map((row, rowIndex) => {
          const getValue = (key: keyof DetailTemplate) => {
            const index = columnMapping[key]?.index;
            return index !== undefined ? row[index] : undefined;
          };

          const addError = (field: keyof DetailTemplate) => {
            validationErrors.push({
              row: rowIndex + 1,
              field: field as string,
              fieldName: HEADERS[field.toUpperCase() as keyof typeof HEADERS] || field
            });
          };

          return {
            stt: Number(getValue('stt') || 0),
            stt_in: Number(getValue('stt_in') || 0),
            ma_so: String(getValue('ma_so') || '').trim(),
            ma_in: String(getValue('ma_in') || '').trim(),
            chi_tieu: String(getValue('chi_tieu') || '').trim(),
            chi_tieu2: String(getValue('chi_tieu2') || '').trim(),
            chi_tieu3: String(getValue('chi_tieu3') || '').trim(),
            thuyet_minh: String(getValue('thuyet_minh') || '').trim(),
            ts_nv: mapValue(getValue('ts_nv'), MAPPINGS.TS_NV, '1', () => addError('ts_nv')),
            in_yn: mapValue(getValue('in_yn'), MAPPINGS.IN_YN, '1', () => addError('in_yn')),
            bold_yn: mapValue(getValue('bold_yn'), MAPPINGS.BOLD_YN, '1', () => addError('bold_yn')),
            loai: mapValue(getValue('loai'), MAPPINGS.LOAI, '1', () => addError('loai')),
            no_co: mapValue(getValue('no_co'), MAPPINGS.NO_CO, '1', () => addError('no_co')),
            dau_cuoi: mapValue(getValue('dau_cuoi'), MAPPINGS.DAU_CUOI, '1', () => addError('dau_cuoi')),
            cong_no: mapValue(getValue('cong_no'), MAPPINGS.CONG_NO, '0', () => addError('cong_no')),
            dau: mapValue(getValue('dau'), MAPPINGS.DAU, '0', () => addError('dau')),
            tk: splitCodes(getValue('tk')),
            tk_du: splitCodes(getValue('tk_du')),
            tk_gt: splitCodes(getValue('tk_gt')),
            cong_thuc: String(getValue('cong_thuc') || '').trim(),
            dieu_kien_khac: CONDITION_MAPPING.map(c => ({
              id_dt_loc: c.id,
              ds_loc: splitCodes(row[columnMapping[`condition_${c.id}`]?.index])
            })).filter(c => c.ds_loc.length > 0)
          };
        });

      if (validationErrors.length > 0) {
        onValidationErrors?.({ errors: validationErrors, columnMapping });
        return;
      }

      if (templates.length === 0) {
        toast({
          description: 'Không có dữ liệu hợp lệ để import',
          variant: 'destructive'
        });
        return;
      }

      await onSubmit(templates);
      toast({
        description: `Đã import thành công ${templates.length} bản ghi`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Lỗi khi đọc file Excel:', error);
      toast({
        description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel',
        variant: 'destructive'
      });
    }
  };

  input.click();
};
